/* Phase 17.1 -- GlassSurface primitive.
 *
 * Single source of truth for translucent surfaces. Kills the drift where
 * some code used `glass-elevated`, some used raw `bg-white/[0.03]`, some
 * used `bg-white/5`. From now on: use this component.
 *
 * Variants (per roadmap A.1):
 *   card     -- default surface, ~4% opacity, subtle border, soft shadow
 *   elevated -- 6% opacity, slightly denser border. Use for panels that
 *               need to feel one level "up" from the page background.
 *   inset    -- 2% opacity, no shadow. Use for a nested block inside a
 *               card (e.g. a list-row within a card).
 *   panel    -- 8% opacity, larger radius. Use for sticky/side panels.
 *
 * All variants include backdrop-blur so the gradient background bleeds
 * through. Pass `as` to render a different element (default `div`).
 *
 * ESLint enforcement: `no-restricted-syntax` blocks raw `bg-white/[0.`
 * class strings in JSX (see .eslintrc). New code MUST use this.
 */
import React from "react";
import { cn } from "@/lib/utils";

const VARIANT_CLASSES = {
  card:
    "bg-white/[0.04] border border-white/10 rounded-2xl shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_10px_30px_-15px_rgba(0,0,0,0.6)] backdrop-blur-md",
  elevated:
    "bg-white/[0.06] border border-white/15 rounded-2xl shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_20px_40px_-20px_rgba(0,0,0,0.7)] backdrop-blur-md",
  inset:
    "bg-white/[0.02] border border-white/[0.06] rounded-xl backdrop-blur-sm",
  panel:
    "bg-white/[0.08] border border-white/20 rounded-3xl shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_24px_50px_-24px_rgba(0,0,0,0.75)] backdrop-blur-xl",
};

export const GlassSurface = React.forwardRef(function GlassSurface(
  { variant = "card", as: Comp = "div", className, children, ...rest },
  ref,
) {
  const base = VARIANT_CLASSES[variant] || VARIANT_CLASSES.card;
  return (
    <Comp
      ref={ref}
      data-glass-surface={variant}
      className={cn(base, className)}
      {...rest}
    >
      {children}
    </Comp>
  );
});

export default GlassSurface;
